import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SwissIcons } from '../components/icons/SwissIconSystem';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    organization: 'Norwegian Refugee Council'
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const success = await signup(
        formData.name,
        formData.email,
        formData.password,
        formData.organization
      );

      if (success) {
        navigate('/demos');
      } else {
        setError('Email already exists or signup failed');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <button onClick={() => navigate('/')} className="inline-block">
            <h1 className="text-4xl font-bold text-ink-900 tracking-wide wordmark">CLEAR</h1>
          </button>
          <p className="mt-2 text-sm text-ink-600">
            Crisis Learning, Early-warning, Anticipation, and Response
          </p>
          <h2 className="mt-6 text-2xl font-semibold text-ink-900">
            Create your NRC account
          </h2>
          <p className="mt-2 text-sm text-ink-600">
            Get access to CLEAR demo pages
          </p>
        </div>

        {/* Signup Form */}
        <div className="bg-white border-2 border-ink-200 p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="border-2 border-red-500 bg-red-50 p-3 flex items-start gap-2">
                <SwissIcons.Alert size={16} className="text-red-500 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-ink-900 mb-2">
                Full name
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border-2 border-ink-200 bg-white text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ink-900 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border-2 border-ink-200 bg-white text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                placeholder="your.email@nrc.no"
              />
            </div>

            <div>
              <label htmlFor="organization" className="block text-sm font-medium text-ink-900 mb-2">
                Organization
              </label>
              <select
                id="organization"
                name="organization"
                required
                value={formData.organization}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border-2 border-ink-200 bg-white text-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
              >
                <option value="Norwegian Refugee Council">Norwegian Refugee Council</option>
                <option value="NRC Partner Organization">NRC Partner Organization</option>
                <option value="UN Agency">UN Agency</option>
                <option value="Other NGO">Other NGO</option>
              </select>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ink-900 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border-2 border-ink-200 bg-white text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink-900 mb-2">
                Confirm password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border-2 border-ink-200 bg-white text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                placeholder="Repeat your password"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 border-2 border-accent-500 text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <>
                    <div className="inline-block h-4 w-4 animate-spin border-2 border-solid border-white border-r-transparent mr-2"></div>
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </button>
            </div>
          </form>

          {/* Sign In Link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-ink-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium text-accent-500 hover:text-accent-600 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-ink-600 hover:text-accent-500 transition-colors inline-flex items-center gap-1"
          >
            <SwissIcons.ChevronLeft size={14} />
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
